import React, { ReactNode, useState } from "react";
import styled, { css } from "styled-components";
import { CircleAlert, CircleCheck, Clock, LucideIcon } from "lucide-react";
import {
  LanguageNameSpaces,
  SubGroupReview,
  SubGroupReviewStatus,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { formatDate } from "@shared/utils/date";
import { MARKETPLACE_REVIEW_GUIDELINES_ARTICLE } from "@shared/features/newMarketplace/constants";
import { getRejectedReviewMessageContent } from "@shared/features/newMarketplace/utils.ts";
import Button from "common/Buttons/Button";
import IntercomLink from "common/Text/IntercomLink";
import Panel from "common/NewLook/Panel.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { Stars } from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/NewLookReviewsList";

interface NewLookCurrentUserReviewProps {
  currentUserReview: SubGroupReview;
  isEdit: boolean;
  onEdit: () => void;
}

const Notice = ({
  icon: Icon,
  warning,
  children,
}: {
  icon: LucideIcon;
  warning?: boolean;
  children: ReactNode;
}) => (
  <NoticeBox role="status" $warning={warning}>
    <Icon size={16} strokeWidth={1.75} aria-hidden={true} />
    <div>{children}</div>
  </NoticeBox>
);

/**
 * The user's own review: its moderation state as a quiet notice, then the
 * review itself in a panel with an Edit action.
 */
const NewLookCurrentUserReview = ({
  currentUserReview,
  isEdit,
  onEdit,
}: NewLookCurrentUserReviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const [rejectionVisible, setRejectionVisible] = useState(true);
  const { status } = currentUserReview;
  const isPending = status === SubGroupReviewStatus.PENDING;
  const isApproved = status === SubGroupReviewStatus.APPROVED;
  const isRejected = status === SubGroupReviewStatus.REJECTED;
  const showReview = !isEdit && status !== SubGroupReviewStatus.DEACTIVATED;

  const editReview = () => {
    onEdit();
    setRejectionVisible(false);
  };

  return (
    <Stack>
      {isPending && (
        <Notice icon={Clock}>{t("reviews.checking_review")}</Notice>
      )}
      {isApproved && !currentUserReview.approved_viewed && (
        <Notice icon={CircleCheck}>{t("reviews.review_approved")}</Notice>
      )}
      {isRejected && rejectionVisible && (
        <Notice icon={CircleAlert} warning={true}>
          {getRejectedReviewMessageContent(
            currentUserReview.rejection_reason,
            t
          )}{" "}
          <GuidelinesLink articleId={MARKETPLACE_REVIEW_GUIDELINES_ARTICLE}>
            {t("reviews.review_guidelines_link")}
          </GuidelinesLink>
          <NoticeActions>
            <Button
              size="small"
              type="text"
              onClick={() => setRejectionVisible(false)}
            >
              {t("reviews.dismiss")}
            </Button>
            <Button size="small" onClick={editReview}>
              {t("reviews.edit_review_button")}
            </Button>
          </NoticeActions>
        </Notice>
      )}
      {showReview && (
        <Panel
          title={t("reviews.your_review")}
          actions={
            (isApproved || isPending) && (
              <EditButton type="button" onClick={editReview}>
                {t("reviews.edit")}
              </EditButton>
            )
          }
        >
          <Review $faded={isPending || isRejected}>
            <Top>
              <Name>{currentUserReview.full_name}</Name>
              <Stars value={currentUserReview.stars} />
            </Top>
            <Meta>
              {formatDate(currentUserReview.creation_timestamp, false, true)}
            </Meta>
            {!!currentUserReview.message && (
              <Message>{currentUserReview.message}</Message>
            )}
          </Review>
        </Panel>
      )}
    </Stack>
  );
};

export default NewLookCurrentUserReview;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  text-align: left;
  &:empty {
    display: none;
  }
`;
const NoticeBox = styled.div<{ $warning?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.body};
  font-size: 13px;
  color: ${(p) => getNewLookTokens(p.theme).text};
  svg {
    flex: none;
    margin-top: 2px;
    color: ${(p) =>
      p.$warning
        ? getNewLookTokens(p.theme).stop
        : getNewLookTokens(p.theme).secondary};
  }
`;
const NoticeActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`;
const GuidelinesLink = styled(IntercomLink)`
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font-weight: 500;
  white-space: nowrap;
`;
const EditButton = styled.button`
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  font-family: inherit;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
const Review = styled.div<{ $faded?: boolean }>`
  ${(p) =>
    p.$faded &&
    css`
      opacity: 0.6;
    `}
`;
const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Name = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Meta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Message = styled.p`
  margin: 8px 0 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: pre-line;
  overflow-wrap: anywhere;
`;
